import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/app/store/toast.store';
import { useOptionalConsent } from '@/shared/hooks/use-optional-consent';
import { useStaffTermsList } from '@/shared/hooks/use-terms';

import { TermsListSection } from './TermsListSection';

export function MedicalTermsPoliciesPage() {
  const { t } = useTranslation(['termsPolicies', 'common']);
  // Per-user list: each row carries `agreed`, so the opt-in button reflects the server, not
  // client-only state. `agree` (from the same server-backed hook) records + refetches.
  const { data: terms, isLoading, isError, refetch } = useStaffTermsList();
  const { agree } = useOptionalConsent();

  // The BE list is dynamic (admin-owned) and order is unspecified; sort by typeId
  // ascending (matches admin id order) for a stable display.
  const sorted = useMemo(() => {
    if (!terms) return [];
    return [...terms].sort((a, b) => a.typeId - b.typeId);
  }, [terms]);

  // Which optional terms this staff has already agreed to — straight from the server list.
  const agreedOptional = useMemo(
    () => new Set(sorted.filter((term) => term.agreed).map((term) => term.typeId)),
    [sorted],
  );

  const handleAgree = useCallback(
    async (typeId: number) => {
      try {
        await agree(typeId);
        toast.success(t('consent.success', { ns: 'termsPolicies' }));
      } catch {
        toast.error(t('load.error', { ns: 'termsPolicies' }));
      }
    },
    [agree, t],
  );

  return (
    <div className="settings-page">
      <h1 className="content__title settings-page__title">{t('title')}</h1>
      <p className="content__subtitle settings-page__subtitle">{t('subtitle')}</p>

      {isLoading ? (
        <div className="settings-section">{/* Loading skeleton */}</div>
      ) : isError || sorted.length === 0 ? (
        <div className="settings-section">
          <p className="form-field__error">{t('load.error')}</p>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => refetch()}
          >
            {t('load.retry')}
          </button>
        </div>
      ) : (
        <TermsListSection
          terms={sorted}
          agreedOptional={agreedOptional}
          onAgree={handleAgree}
        />
      )}
    </div>
  );
}

export default MedicalTermsPoliciesPage;
